import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { useSeo } from '@/seo/useSeo';
import { injectWebPageSchema, injectArticleSchema, injectFaqSchema, injectBreadcrumbSchema } from '@/seo/schema';

interface FaqItem {
  q: string;
  a: string;
}

export default function ArticlePage() {
  const { t } = useTranslation();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const articlePath = '/article/allchinabuy-spreadsheet-guide';
  const articleTitle = 'AllChinaBuy Spreadsheet Guide 2026 — Complete Resource';
  const articleDescription =
    'Discover how to use the AllChinaBuy spreadsheet effectively to find the best products, verify sellers, and save on shipping costs. Complete 2026 guide with FAQ, categories, and safety tips.';

  const faqItems: FaqItem[] = [
    { q: 'faq_q1', a: 'faq_a1' },
    { q: 'faq_q2', a: 'faq_a2' },
    { q: 'faq_q3', a: 'faq_a3' },
    { q: 'faq_q4', a: 'faq_a4' },
    { q: 'faq_q5', a: 'faq_a5' },
  ];

  useSeo({
    title: `${articleTitle} | allchinabuyspread.de`,
    description: articleDescription,
    keywords:
      'AllChinaBuy Spreadsheet, AllChinaBuy Guide, AllChinaBuy Finds, AllChinaBuy Shipping, AllChinaBuy Buying Guide',
    path: articlePath,
    type: 'article',
  });

  useEffect(() => {
    injectWebPageSchema(articlePath, articleTitle, articleDescription);
    injectArticleSchema({
      headline: articleTitle,
      description: articleDescription,
      path: articlePath,
      datePublished: '2026-08-01',
      dateModified: '2026-08-25',
    });
    injectFaqSchema([
      { q: t('faq_q1'), a: t('faq_a1') },
      { q: t('faq_q2'), a: t('faq_a2') },
      { q: t('faq_q3'), a: t('faq_a3') },
      { q: t('faq_q4'), a: t('faq_a4') },
      { q: t('faq_q5'), a: t('faq_a5') },
    ]);
    injectBreadcrumbSchema([
      { name: t('breadcrumb_home'), path: '/' },
      { name: 'AllChinaBuy Spreadsheet Guide', path: articlePath },
    ]);
  }, [t, articlePath, articleTitle, articleDescription]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      
      <main>
        {/* Article Header */}
        <section className="w-full pt-24 pb-10 md:pt-28 md:pb-14 bg-white border-b border-background-200/50">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav aria-label="breadcrumb" className="mb-4">
              <ol className="flex items-center gap-2 text-xs text-foreground-500">
                <li>
                  <a href="/" className="hover:text-primary-600 transition-colors">{t('breadcrumb_home')}</a>
                </li>
                <li><i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i></li>
                <li className="text-foreground-400">AllChinaBuy Spreadsheet Guide</li>
              </ol>
            </nav>

            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium uppercase tracking-wider mb-3">
              Guide 2026
            </span>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-900 tracking-tight mb-4">
              {t('article_h1')}
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
              {t('article_intro')}
            </p>
          </div>
        </section>

        {/* Article Body */}
        <section className="w-full py-10 md:py-14">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-4xl mx-auto">
            <article className="prose prose-sm md:prose-base max-w-none">
              
              {/* What is */}
              <div className="mb-10 md:mb-12">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 tracking-tight mb-4">
                  {t('article_what_is_title')}
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {t('article_what_is_p1')}
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {t('article_what_is_p2')}
                </p>
              </div>

              {/* How to use */}
              <div className="mb-10 md:mb-12 bg-background-100 rounded-xl p-5 md:p-7">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 tracking-tight mb-5">
                  {t('article_how_to_title')}
                </h2>
                <ol className="space-y-4">
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-500 text-white text-xs font-bold flex items-center justify-center mt-0.5">1</span>
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{t('article_how_to_step1')}</p>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-500 text-white text-xs font-bold flex items-center justify-center mt-0.5">2</span>
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{t('article_how_to_step2')}</p>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-500 text-white text-xs font-bold flex items-center justify-center mt-0.5">3</span>
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{t('article_how_to_step3')}</p>
                  </li>
                  <li className="flex gap-3">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-500 text-white text-xs font-bold flex items-center justify-center mt-0.5">4</span>
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">{t('article_how_to_step4')}</p>
                  </li>
                </ol>
              </div>

              {/* Popular Categories */}
              <div className="mb-10 md:mb-12">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 tracking-tight mb-5">
                  {t('article_categories_title')}
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-white border border-background-200/60 hover:border-primary-300/50 hover:bg-primary-50/30 transition-all text-sm text-foreground-700 hover:text-primary-700 cursor-pointer"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400 shrink-0"></span>
                      {t(cat.nameKey)}
                    </a>
                  ))}
                </div>
              </div>

              {/* Safety Tips */}
              <div className="mb-10 md:mb-12">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 tracking-tight mb-4">
                  {t('article_safety_title')}
                </h2>
                <div className="bg-amber-50/70 border border-amber-200/50 rounded-xl p-5 md:p-6 mb-4">
                  <div className="flex gap-3">
                    <i className="ri-alert-line text-amber-600 w-5 h-5 flex items-center justify-center shrink-0 mt-0.5"></i>
                    <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                      {t('article_safety_p1')}
                    </p>
                  </div>
                </div>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {t('article_safety_p2')}
                </p>
              </div>

              {/* Shipping */}
              <div className="mb-10 md:mb-12">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 tracking-tight mb-4">
                  {t('article_shipping_title')}
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                  {t('article_shipping_p1')}
                </p>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {t('article_shipping_p2')}
                </p>
              </div>

              {/* FAQ */}
              <div className="mb-10 md:mb-12">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 tracking-tight mb-5">
                  {t('article_faq_title')}
                </h2>
                <div className="space-y-3">
                  {faqItems.map((item, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-xl border border-background-200/60 overflow-hidden"
                    >
                      <button
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                        className="w-full flex items-center justify-between gap-3 p-4 md:p-5 text-left cursor-pointer"
                      >
                        <span className="text-sm md:text-base font-semibold text-foreground-900">
                          {t(item.q)}
                        </span>
                        <i className={`ri-${openFaq === index ? 'subtract' : 'add'}-line w-5 h-5 flex items-center justify-center text-foreground-500 shrink-0 transition-transform`}></i>
                      </button>
                      {openFaq === index && (
                        <div className="px-4 md:px-5 pb-4 md:pb-5">
                          <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                            {t(item.a)}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Conclusion */}
              <div className="mb-10 md:mb-12">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 tracking-tight mb-4">
                  {t('article_conclusion_title')}
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                  {t('article_conclusion')}
                </p>
              </div>

              {/* CTA */}
              <div className="bg-primary-50/50 rounded-xl p-6 md:p-8 text-center">
                <h3 className="text-lg md:text-xl font-bold text-foreground-900 mb-3">
                  {t('article_cta')}
                </h3>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href="/"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-primary-600 text-sm font-semibold border border-primary-200 hover:bg-primary-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
                    {t('breadcrumb_home')}
                  </a>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    {t('hero_cta_secondary')}
                    <i className="ri-external-link-line w-4 h-4 flex items-center justify-center"></i>
                  </a>
                </div>
              </div>

              {/* Internal link back to home */}
              <div className="mt-8 pt-6 border-t border-background-200/50">
                <p className="text-sm text-foreground-500">
                  Back to{' '}
                  <a href="/" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
                    {t('breadcrumb_home')}
                  </a>
                  {' '}— Explore more AllChinaBuy resources.
                </p>
              </div>
            </article>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="w-full py-8 bg-white border-t border-background-200/40">
          <div className="w-full px-4 md:px-6 lg:px-8 max-w-4xl mx-auto text-center">
            <p className="text-xs text-foreground-500 leading-relaxed">
              {t('disclaimer')}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}